// cspell: words ndarray coeffs extrinsics

import _ from 'lodash';
import { CameraConfig, CamerasInWorld, Extrinsic, Intrinsics, SimplifiedCameraConfig } from './camera.types';

type CameraCalibrationSetup = {
    camera_calibration_id: number;
    camera_setup_id: number;
    created_at: string;
    id: number;
    camera_setups: {
        booth_id: number;
        camera_id: string;
        camera_lens_type_id: number | null;
        camera_type_id: number;
        created_at: string;
        id: number;
        is_active: boolean;
        left_handed_position_id: number | null;
        right_handed_position_id: number | null;
        camera_types: {
            created_at: string;
            id: number;
            image_height: number;
            image_width: number;
            name: string;
            sensor_height_mm: number;
            sensor_width_mm: number;
        };
    };
};

/**
 * Converts a detailed camera configuration into a simplified format where configurations
 * are accessed directly by camera names.
 */
export const convertToSimplifiedConfig = (
    configFromServer: CameraConfig,
    cameraCalibrations?: CameraCalibrationSetup[],
): SimplifiedCameraConfig => {
    const cameraIdToName = configFromServer.camera_id_to_name;

    const intrinsics: { [key: string]: Intrinsics } = {};

    _(configFromServer.intrinsics)
        .keys()
        .forEach((cameraId) => {
            const cameraName = cameraIdToName[cameraId];
            const intr = configFromServer.intrinsics[cameraId];
            intrinsics[cameraName] = {
                fx: intr.fx,
                fy: intr.fy,
                cx: intr.cx,
                cy: intr.cy,
                width: intr.width,
                height: intr.height,
                distortion_coeffs: {
                    __ndarray__: _.map(intr.distortion_coeffs.__ndarray__, (x) => parseFloat(x.toFixed(2))),
                },
                distortion_model: intr.distortion_model,
            };
        });

    const extrinsics: { [key: string]: Extrinsic } = {};

    _(configFromServer.extrinsics)
        .keys()
        .forEach((cameraID) => {
            const cameraName = cameraIdToName[cameraID];
            extrinsics[cameraName] = {
                __ndarray__: configFromServer.extrinsics[cameraID].__ndarray__,
            };
        });

    const camerasInWorld: { [key: string]: CamerasInWorld } = {};

    _(configFromServer.cameras_in_world)
        .keys()
        .forEach((cameraID) => {
            const cameraName = cameraIdToName[cameraID];
            camerasInWorld[cameraName] = {
                __ndarray__: configFromServer.cameras_in_world[cameraID].__ndarray__,
            };
        });

    return {
        ...configFromServer,
        intrinsics: intrinsics,
        extrinsics: extrinsics,
        cameras_in_world: camerasInWorld,
        camera_types: cameraCalibrations?.map(({ camera_setups }) => camera_setups.camera_types),
    };
};
